import { useEffect, useState, type CSSProperties } from 'react';
import SanityImage from '@/components/elements/SanityImage/SanityImage';
import ResponsiveImage from '@/components/elements/ResponsiveImage/ResponsiveImage';
import { IMAGE_SIZES } from '@/components/elements/ResponsiveImage/imageSizes';
import type { CoverflowImage } from './galleryMedia';

/** How often the active slide advances. Slow enough to actually look at each photo. */
const AUTO_ADVANCE_MS = 3200;
/** Slides more than this many steps from the active one aren't mounted at all — bounds the
 * DOM size regardless of how many photos a category has (a 40-photo category still only ever
 * renders 5 <img> tags at once). */
const VISIBLE_RADIUS = 2;
/**
 * Every slide — active or not — is this same box, just scaled down for the non-active ones (see
 * slideStyle). Sizing every slide from one shared, viewport-clamped width (not the carousel's own
 * width) is what keeps the composition a compact, centered group of photos instead of a single
 * image stretched across the full carousel: the carousel can be as wide as the page allows
 * without the photos themselves getting any bigger than this.
 */
const BASE_IMAGE_WIDTH = 'clamp(280px, 32vw, 500px)';
/** Every slide crops to this ratio (object-fit: cover) — photos of very different native
 * proportions still read as one consistent visual system instead of some tall, some short. */
const BASE_ASPECT_RATIO = 4 / 3;

/** A faint grain, via feTurbulence — reads as concrete/cement pores at this low an opacity (the
 * alpha channel set by the final `0.06` in the color matrix), not as visible noise. */
const GRAIN_TEXTURE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.06 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/**
 * The carousel's backdrop: a dark, textured "waterproofed concrete" surface, layered back to
 * front so the photos stay the obvious focal point —
 *   1. grain (concrete pores / cement texture, very faint)
 *   2. a few extremely faint droplet/moisture highlights, upper-right
 *   3. faint diagonal teal membrane-inspired tints, only at the far corners
 *   4. a soft glossy sheen along the very bottom edge (wet-surface reflection)
 *   5. two uneven, soft moisture-stain patches (never as a symmetric pattern)
 *   6. a vignette darkening the corners
 *   7. a teal atmospheric glow behind where the active photo sits
 *   8. the base diagonal wash, brand colours only (no blue)
 */
const CAROUSEL_BACKGROUND: CSSProperties = {
  backgroundImage: [
    GRAIN_TEXTURE,
    'radial-gradient(circle at 82% 18%, rgba(255,255,255,0.05) 0%, transparent 3%)',
    'radial-gradient(circle at 90% 27%, rgba(255,255,255,0.045) 0%, transparent 2.5%)',
    'radial-gradient(circle at 75% 12%, rgba(255,255,255,0.04) 0%, transparent 2%)',
    'linear-gradient(125deg, rgba(68,179,136,0.07) 0%, transparent 10%, transparent 90%, rgba(0,136,132,0.06) 100%)',
    'linear-gradient(0deg, rgba(255,255,255,0.06) 0%, transparent 14%)',
    'radial-gradient(42% 28% at 18% 82%, rgba(7,31,39,0.4), transparent 72%)',
    'radial-gradient(34% 24% at 72% 28%, rgba(18,63,75,0.32), transparent 72%)',
    'radial-gradient(120% 120% at 50% 50%, transparent 45%, rgba(0,0,0,0.4) 100%)',
    'radial-gradient(circle at 50% 45%, rgba(22,94,108,0.65) 0%, rgba(22,94,108,0.3) 38%, rgba(7,31,39,0.92) 100%)',
    'linear-gradient(135deg, #243642 0%, #123f4b 50%, #071f27 100%)',
  ].join(', '),
  backgroundSize: '180px 180px, cover, cover, cover, cover, cover, cover, cover, cover, cover, cover',
  backgroundRepeat: 'repeat, no-repeat, no-repeat, no-repeat, no-repeat, no-repeat, no-repeat, no-repeat, no-repeat, no-repeat, no-repeat',
};

/**
 * A category's photos wrap around, so "distance from the active slide" is circular: photo 0 can
 * be one step before photo `length - 1`, not `length - 1` steps after it. With exactly two
 * photos both directions are the same photo, which would otherwise render it twice (once on
 * each side) — so that case always places the other photo on the right instead.
 */
function offsetFromActive(index: number, active: number, length: number): number {
  if (length === 2) return index === active ? 0 : 1;
  let raw = index - active;
  const half = length / 2;
  if (raw > half) raw -= length;
  if (raw < -half) raw += length;
  return raw;
}

/**
 * Size, depth, tilt and horizontal position for a slide this many steps from the active one.
 * Every slide shares the same base box (BASE_IMAGE_WIDTH / BASE_ASPECT_RATIO), so `translateX`
 * as a percentage — which resolves against the element's *own* box — naturally offsets each
 * slide by a fraction of that shared photo size rather than the carousel's width. That's what
 * keeps the group compact and centered instead of spread edge-to-edge.
 */
function slideStyle(offset: number): CSSProperties {
  const distance = Math.abs(offset);
  const side = Math.sign(offset);
  const scale = distance === 0 ? 1 : distance === 1 ? 0.82 : 0.68;
  const shiftPercent = distance === 0 ? 0 : distance === 1 ? 62 : 112;
  const translateZ = distance === 0 ? 50 : 0;
  const rotateY = distance === 0 ? 0 : -side * (distance === 1 ? 10 : 16);
  const opacity = distance === 0 ? 1 : distance === 1 ? 0.85 : 0.5;

  return {
    transform: `translate(-50%, -50%) translateX(${side * shiftPercent}%) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
    zIndex: 30 - distance * 10,
    opacity,
  };
}

const SLIDE_IMAGE_CLASS = 'h-full w-full object-cover';

function CoverflowImageTag({ image }: { image: CoverflowImage }) {
  return image.kind === 'sanity-image' ? (
    <SanityImage image={image.image} alt="" sizes={IMAGE_SIZES.gallery} className={SLIDE_IMAGE_CLASS} />
  ) : (
    <ResponsiveImage
      src={image.src}
      srcSet={image.srcSet}
      sizes={image.srcSet ? IMAGE_SIZES.gallery : undefined}
      alt=""
      className={SLIDE_IMAGE_CLASS}
    />
  );
}

interface CategoryCoverflowProps {
  images: CoverflowImage[];
  /** The category's name — used for accessible labels, never shown as text here (the tile's
   * own caption overlay already shows it). */
  title: string;
}

/**
 * The photo carousel inside a category's featured strip (see CategoryCarouselSection.tsx). A
 * single photo just shows itself, no carousel machinery. Two or more auto-rotate through a 3D
 * coverflow: the active photo centered and largest, its neighbours smaller and tilted back on
 * either side. Hovering pauses the rotation; clicking a side photo jumps to it directly instead
 * of waiting. Fills whatever size its parent gives it — the parent owns the carousel's height.
 */
const CategoryCoverflow: React.FC<CategoryCoverflowProps> = ({ images, title }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    const id = window.setInterval(() => {
      setActive((current) => (current + 1) % images.length);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [paused, images.length]);

  // Nothing to rotate through — show the one photo exactly like an ungrouped tile would.
  // `absolute inset-0`, not h-full: see the note on the coverflow container below for why a
  // percentage height doesn't work against this parent's min-h-* sizing.
  if (images.length <= 1) {
    return images[0] ? (
      <div className="absolute inset-0 overflow-hidden">
        <CoverflowImageTag image={images[0]} />
      </div>
    ) : null;
  }

  const goToPrevious = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setActive((current) => (current - 1 + images.length) % images.length);
  };
  const goToNext = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setActive((current) => (current + 1) % images.length);
  };

  return (
    <div
      // `absolute inset-0` (needs a positioned ancestor — its parent in CategoryCarouselSection
      // is `relative`) rather than `h-full w-full`: the parent's height comes from a min-h-*
      // class, and a percentage height like h-full doesn't resolve against a height that's only
      // set via min-height — it collapses to 0. Filling by inset instead sidesteps that.
      //
      // A textured dark concrete backdrop (see CAROUSEL_BACKGROUND) rather than plain white, so
      // the gaps around and behind the peeking side images (they don't cover the full box) read
      // as a deliberate "waterproofed concrete" surface instead of a hole in a plain card.
      className="absolute inset-0 overflow-hidden"
      style={{ perspective: '1200px', ...CAROUSEL_BACKGROUND }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {images.map((image, index) => {
        const offset = offsetFromActive(index, active, images.length);
        if (Math.abs(offset) > VISIBLE_RADIUS) return null;
        const isActive = offset === 0;

        return (
          <div
            key={image.id}
            role={isActive ? undefined : 'button'}
            tabIndex={isActive ? undefined : 0}
            aria-label={isActive ? undefined : `Show photo ${index + 1} of ${title}`}
            aria-hidden={isActive ? undefined : true}
            onClick={
              isActive
                ? undefined
                : (event) => {
                    // A side photo just re-centers the carousel; only the active one should
                    // trigger the tile's own navigation (the surrounding <Link>).
                    event.preventDefault();
                    event.stopPropagation();
                    setActive(index);
                  }
            }
            onKeyDown={
              isActive
                ? undefined
                : (event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    event.stopPropagation();
                    setActive(index);
                  }
            }
            className="absolute left-1/2 top-1/2 overflow-hidden rounded-lg shadow-lg transition-[transform,opacity] duration-700 ease-out"
            style={{
              width: BASE_IMAGE_WIDTH,
              aspectRatio: BASE_ASPECT_RATIO,
              ...slideStyle(offset),
            }}
          >
            <CoverflowImageTag image={image} />
          </div>
        );
      })}

      <button
        type="button"
        aria-label={`Previous photo in ${title}`}
        onClick={goToPrevious}
        className="absolute left-2 top-1/2 z-50 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg bg-black/40 text-white transition hover:bg-black/60 md:left-4"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <button
        type="button"
        aria-label={`Next photo in ${title}`}
        onClick={goToNext}
        className="absolute right-2 top-1/2 z-50 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg bg-black/40 text-white transition hover:bg-black/60 md:right-4"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );
};

export default CategoryCoverflow;
